<template>
  <div>
    <b-card
      no-body
      class="mb-0"
    >
      <div class="m-2">
        <b-row>
          <b-col
            cols="12"
            md="3"
            xl="2"
          >
            <b-form-group
              label="Ano"
              label-for="filter-year"
            >
              <v-select
                v-model="filterYear"
                :options="yearOptions"
                :reduce="val => val.value"
                :clearable="false"
                input-id="filter-year"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="4"
            xl="2"
          >
            <b-form-group
              label="Mês"
              label-for="filter-month"
            >
              <v-select
                v-model="filterMonth"
                :options="monthOptions"
                :reduce="val => val.value"
                :clearable="false"
                input-id="filter-month"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="5"
            xl="4"
          >
            <label for="institution-id">Instituição</label>
            <v-select
              id="institution-id"
              v-model="institutionId"
              name="institution-id"
              :options="institutionOptions"
              label="label"
              item-value="value"
              item-text="label"
            >
              <span slot="no-options">Nenhuma instituição encontrada.</span>
            </v-select>
          </b-col>
          <b-col
            cols="12"
            md="8"
            xl="4"
          >
            <label class="invisible d-none d-md-block">Ações</label>
            <div class="d-flex justify-content-end">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                class="btn-icon mr-1"
                @click="loadReport"
              >
                Buscar
                <feather-icon
                  icon="SearchIcon"
                  size="16"
                />
              </b-button>
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="outline-primary"
                class="btn-icon"
                :disabled="exporting"
                @click="downloadReport"
              >
                Exportar
                <feather-icon
                  icon="DownloadIcon"
                  size="16"
                />
              </b-button>
            </div>
          </b-col>
        </b-row>
        <b-alert
          show
          variant="primary"
          class="p-50 mb-0 font-small-2 d-inline-block w-auto"
        >
          * A quantidade de antecipação se refere a data da ação de antecipar o serviço
        </b-alert>
      </div>
    </b-card>

    <b-card
      no-body
      class="mt-2 mb-0"
    >
      <b-table
        class="position-relative"
        :items="items"
        :busy="loading"
        responsive
        sticky-header="900px"
        :fields="tableColumns"
        primary-key="row_key"
        show-empty
        empty-text="Nenhum registro encontrado"
      >
        <template #cell(physician_name)="{ item }">
          <div :title="item.physician_name">
            <div>{{ truncateString(item.physician_name, 25) }}</div>
            <div class="font-small-2">
              {{ formatCpf(item.cpf) }}
            </div>
          </div>
        </template>
        <template #cell(crm)="{ item }">
          {{ item.crm_number }} - {{ item.crm_state }}
        </template>
        <template #cell(status)="{ item }">
          <span :title="item.classification_label">
            <feather-icon
              icon="SmartphoneIcon"
              size="20"
              :color="getClassificationColor(item.classification_color)"
            />
          </span>
        </template>
        <template #cell(anticipations_current_month)="{ item }">
          <span
            :id="`anticipations-current-${item.row_key}`"
            class="font-weight-bolder"
          >
            {{ item.anticipations_current_month ? item.anticipations_current_month.count : 0 }} / {{ formatCurrencyInt(item.anticipations_current_month ? item.anticipations_current_month.value : 0) }}
          </span>
          <b-tooltip
            v-if="item.anticipations_current_month && item.anticipations_current_month.breakdown && item.anticipations_current_month.breakdown.length"
            :target="`anticipations-current-${item.row_key}`"
            placement="top"
          >
            <div
              v-for="entry in item.anticipations_current_month.breakdown"
              :key="entry.competence"
            >
              {{ formatCompetence(entry.competence) }}: {{ entry.count }} / {{ formatCurrencyInt(entry.value) }}
            </div>
          </b-tooltip>
        </template>
        <template #cell(launched_quantity)="{ item }">
          <span
            :id="`launched-quantity-${item.row_key}`"
            class="font-weight-bolder"
          >
            {{ item.launched_quantity ? item.launched_quantity : 0 }} / {{ formatCurrencyInt(item.launched_value ? item.launched_value : 0) }}
          </span>
        </template>
        <template #cell(anticipations_previous_month)="{ item }">
          <span
            :id="`anticipations-previous-${item.row_key}`"
            class="font-weight-bolder"
          >
            {{ item.anticipations_previous_month ? item.anticipations_previous_month.count : 0 }} / {{ formatCurrencyInt(item.anticipations_previous_month ? item.anticipations_previous_month.value : 0) }}
          </span>
          <b-tooltip
            v-if="item.anticipations_previous_month && item.anticipations_previous_month.breakdown && item.anticipations_previous_month.breakdown.length"
            :target="`anticipations-previous-${item.row_key}`"
            placement="top"
          >
            <div
              v-for="entry in item.anticipations_previous_month.breakdown"
              :key="entry.competence"
            >
              {{ formatCompetence(entry.competence) }}: {{ entry.count }} / {{ formatCurrencyInt(entry.value) }}
            </div>
          </b-tooltip>
        </template>
        <template #cell(anticipations_two_months_ago)="{ item }">
          <span
            :id="`anticipations-two-${item.row_key}`"
            class="font-weight-bolder"
          >
            {{ item.anticipations_two_months_ago ? item.anticipations_two_months_ago.count : 0 }} / {{ formatCurrencyInt(item.anticipations_two_months_ago ? item.anticipations_two_months_ago.value : 0) }}
          </span>
          <b-tooltip
            v-if="item.anticipations_two_months_ago && item.anticipations_two_months_ago.breakdown && item.anticipations_two_months_ago.breakdown.length"
            :target="`anticipations-two-${item.row_key}`"
            placement="top"
          >
            <div
              v-for="entry in item.anticipations_two_months_ago.breakdown"
              :key="entry.competence"
            >
              {{ formatCompetence(entry.competence) }}: {{ entry.count }} / {{ formatCurrencyInt(entry.value) }}
            </div>
          </b-tooltip>
        </template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import store from '@/store'
import Ripple from 'vue-ripple-directive'
import { onUnmounted } from '@vue/composition-api'
import vSelect from 'vue-select'
import {
  BCard, BRow, BCol, BFormGroup, BButton, BTable, BAlert, BTooltip,
} from 'bootstrap-vue'
import reportStoreModule from '../../reportStoreModule'
import useMonthlyProfessionalWorkplaceReport from './useMonthlyProfessionalWorkplaceReport'

export default {
  directives: {
    Ripple,
  },
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BButton,
    BTable,
    BAlert,
    BTooltip,
    vSelect,
  },
  setup() {
    const REPORTS_STORE_MODULE_NAME = 'app-reports'
    if (!store.hasModule(REPORTS_STORE_MODULE_NAME)) store.registerModule(REPORTS_STORE_MODULE_NAME, reportStoreModule)

    onUnmounted(() => {
      if (store.hasModule(REPORTS_STORE_MODULE_NAME)) store.unregisterModule(REPORTS_STORE_MODULE_NAME)
    })

    const {
      loading,
      filterYear,
      filterMonth,
      yearOptions,
      monthOptions,
      institutionId,
      institutionOptions,
      fetchInstitutions,
      items,
      tableColumns,
      getClassificationColor,
      loadReport,
      downloadReport,
      exporting,
      truncateString,
      formatCpf,
      formatCurrencyInt,
      formatCompetence,
    } = useMonthlyProfessionalWorkplaceReport()

    fetchInstitutions()
    loadReport()

    return {
      loading,
      filterYear,
      filterMonth,
      yearOptions,
      monthOptions,
      institutionId,
      institutionOptions,
      items,
      tableColumns,
      getClassificationColor,
      loadReport,
      downloadReport,
      exporting,
      truncateString,
      formatCpf,
      formatCurrencyInt,
      formatCompetence,
    }
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

label {
  margin-top: 0.2857rem!important;
}
</style>
